import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EnvironmentService } from '@evorait/pace-environment';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { defer, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { ENDPOINTS } from '../../../enums/endpoints.enum';
import { UserMasterInstanceToken, UserMasterInterface } from '../../../interfaces/user-master.interface';
import { saveLocationTrackerUserConfigSuccess } from '../../location-tracker/actions/location-tracker.actions';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import {
    appConfigFailed,
    appConfigLoaded,
    appConfigLoadedFromStorage,
    loadAppConfig,
} from '../actions/app-config.actions';
import { appConfigResponseToState } from '../app-config.converters';
import { APP_CONFIG_FEATURE_KEY, AppConfigValuesState, createDefaultAppConfigValuesState } from '../app-config.state';
import { AppConfigResponse } from '../app-config.wire';
import { selectAppConfigLoaded } from '../selectors/app-config.selector';

/**
 * Owns the app-config load/refresh flow and the persistence mirror. `AppConfigService` was never
 * wired up live, so there is no legacy implementation to keep in sync with — this is the only
 * implementation, and consumers talk to the store directly instead of through a facade/injection-
 * token pair (see `LocationTrackerEffects` for the precedent).
 *
 * `refreshOnLocationTrackerSave$` reacts to `saveLocationTrackerUserConfigSuccess` rather than
 * being called from inside `LocationTrackerEffects.saveLocationTrackerUserConfig$`: decoupling the
 * two means a failing app-config refresh can no longer turn a successful location-tracker save
 * into a reported failure.
 */
@Injectable()
export class AppConfigEffects {
    private readonly url: string;

    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly http: HttpClient,
        private readonly environmentService: EnvironmentService,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.url = this.environmentService.apiBase + ENDPOINTS.AppConfigSet;
    }

    /**
     * Fetch the app config from the backend and populate the slice.
     *
     * The country read sits inside the `defer` on purpose: `getSelectedUser()` THROWS when the
     * user master is not initialized or no user has been selected, and a throw in the `switchMap`
     * project function would kill this effect without ever dispatching {@link appConfigFailed} —
     * leaving `awaitAppConfigSettled` waiting forever and stalling app initialization. Deferring
     * turns that throw into an error notification, so `catchError` reports it like any failed
     * request and the boot chain surfaces it as an `InitError`, exactly as the legacy
     * `AppConfigService.fetchConfig` did.
     */
    public loadAppConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadAppConfig),
            switchMap(() =>
                defer(() => {
                    const country = this.userMasterService.getSelectedUser().country;
                    return this.http.get<AppConfigResponse>(`${this.url}?country=${country}`);
                }).pipe(
                    map((response) => appConfigLoaded({ values: appConfigResponseToState(response) })),
                    catchError((error) => {
                        this.log.error(
                            `AppConfigEffects: loadAppConfig$: Failed to fetch app config from ${this.url}:`,
                            error,
                        );
                        return of(appConfigFailed());
                    }),
                ),
            ),
        ),
    );

    /**
     * Refresh the app config once a location-tracker save has succeeded — the `LocationTracker`
     * admin settings live here too, so the location-tracker page's read-only values need
     * re-fetching after a save. Replaces the old, coupled `AppConfigService.refreshAppConfig()`
     * call inside `LocationTrackerEffects.saveLocationTrackerUserConfig$`.
     */
    public refreshOnLocationTrackerSave$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveLocationTrackerUserConfigSuccess),
            map(() => loadAppConfig()),
        ),
    );

    /** Rehydrate from the cache on init, but only while the backend has not yet populated the slice. */
    public hydrateAppConfigOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadAppConfig),
            concatLatestFrom(() => this.store.select(selectAppConfigLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => hydrateSlice({ featureKey: APP_CONFIG_FEATURE_KEY })),
        ),
    );

    /** Bridge the generic hydrate result into the slice, unless the backend already won. */
    public applyHydratedAppConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === APP_CONFIG_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectAppConfigLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) =>
                appConfigLoadedFromStorage({
                    values: (data as AppConfigValuesState | undefined) ?? createDefaultAppConfigValuesState(),
                }),
            ),
        ),
    );

    /** Cache the slice whenever the backend load populates it. */
    public persistAppConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(appConfigLoaded),
            map(() => persistSlice({ featureKey: APP_CONFIG_FEATURE_KEY })),
        ),
    );
}
